/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useCallback } from 'react';
import { FaCircleCheck } from 'react-icons/fa6';
import { useErrorState } from '../../shared/useErrorState';
import { VStack, HStack, Box } from 'chakra-v3';
import type { UserMCQComponent } from '@karya/core';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedParams, getTypedComponent } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import { Button } from '../../../../../components/buttons/Button';
import { NoOptionsError, MinSelectionError, MaxSelectionError } from './ErrorMessages';
import { useNext } from '../../../hooks/useNext';

export const UserMCQ: React.FC<ComponentRendererProps> = ({ node, isCurrent, controller }) => {
  const params = getTypedParams<UserMCQComponent>(node);
  const component = getTypedComponent<UserMCQComponent>(node);
  const options = params.options ?? [];
  // Ensure min is at least 1
  const validMin = Math.max(params.min ?? 1, 1);
  // Ensure max is within bounds - [validMin, options.length]
  const rawMax = params.max ?? options.length;
  const validMax = Math.max(Math.min(rawMax, options.length), validMin);

  const [error, setError] = useErrorState();
  const iconColor = 'var(--chakra-colors-brand-secondaryBlue2)';

  const handleToggle = (option: string) => {
    if (!isCurrent) {
      return;
    }

    let selected: string[] = params.selected ?? [];

    if (selected.length <= validMin && selected.includes(option)) {
      // Min selection reached, do not allow deselection
      setError(() => new MinSelectionError(validMin));
      return;
    }

    if (selected.length >= validMax && !selected.includes(option)) {
      // Max selection reached, do not allow more selections
      setError(() => new MaxSelectionError(validMax));
      selected = selected.slice(1); // Deselect the first selected option
    }

    if (selected?.includes(option)) {
      // Deselect option
      selected = selected.filter((o) => o !== option);
    } else {
      // Select option
      selected = [...selected, option];
    }

    controller.writeOutput(component.selected, selected);
  };

  const teardownFn = useCallback(
    (next: () => void) => {
      // Validate options array
      if (options.length === 0) {
        setError(() => new NoOptionsError());
        return;
      }

      const numOptionsSelected = params.selected?.length ?? 0;
      if (numOptionsSelected < validMin) {
        setError(() => new MinSelectionError(validMin));
        return;
      }
      if (numOptionsSelected > validMax) {
        setError(() => new MaxSelectionError(validMax));
        return;
      }

      setError(null);

      // Only call next() when validation passes and output is written
      next();
    },
    [options.length, params.selected, validMin, validMax, setError],
  );

  useNext({
    controller,
    teardownFn,
    strategy: 'MANUAL',
    isCurrent,
  });

  return (
    <Box w="100%" bg="transparent" display="flex" justifyContent="center" position="relative" zIndex={1} minH="100px">
      <VStack gap="16px" align="stretch" w="100%" maxW="100%" minW="100%">
        {error && (
          <Text variant="bodyBold" color="text.accent" alignSelf="flex-end">
            {error.message}
          </Text>
        )}
        <HStack gap="16px" wrap="wrap" align="center" w="100%">
          {/* Single Select Layout */}
          {options.map((option) => {
            const isSelected = params.selected?.includes(option);
            return (
              <Button
                key={option}
                variant={isSelected ? 'selectedWithIcon' : 'secondary'}
                onClick={() => handleToggle(option)}
                disabled={!isCurrent}
                minWidth="max-content"
                flex={1}
                justifyContent="flex-start"
              >
                {isSelected && <FaCircleCheck size={24} color={iconColor} />}
                <Text variant="bodyBold" flex={1}>
                  {option}
                </Text>
              </Button>
            );
          })}
        </HStack>
      </VStack>
    </Box>
  );
};
